import { ButtonType, ButtonWidget, DropdownButtonType, List, ListItem } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as React from 'react';
import { style } from 'typestyle';
import * as _ from 'underscore';

import { FormikSingleSelectDropdown } from '~src/commonInfra/components/FormikSingleSelectDropdown';
import { OAuthCredentialsService } from '~src/connectivity/services/OAuthCredentialsService';
import { UserSettingsStyle } from '~src/userSettings/styles/UserSettingsStyle';

import { Messages } from '../../../../../codegen/Localize';
import { FormikTextBox } from '../../../../../commonInfra/components/FormikField';
import { textFieldWidget } from '../../../../../userSettings/styles/EmailAndDisplayNameFormStyles';

export interface OAuthClientSetting {
  dbClass: string;
  instanceUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthClientRedirectUri: string;
  id: string;
}

export type OAuthClientsFormField = { oauthClients: OAuthClientSetting[] }
export type OAuthClientsFormikProps = FormikProps<OAuthClientsFormField>;

export interface AddOAuthClientDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  addOAuthClientSubmit: (values: AddOAuthClientDialogState) => void;
  hardcodeConnectionType?: string;
  showInstanceUrlSection: boolean;
}

export interface AddOAuthClientDialogState {
  dbClass: string;
  instanceUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthClientRedirectUri: string;
}

const dropdownStyle = style({
  width: 300
});

// revise the regex pattern from https://www.tutorialspoint.com/How-to-validate-URL-address-in-JavaScript
const urlPattern = new RegExp('^(https?:\\/\\/)' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{1,}|localhost|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
  '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
  '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator

// redirectUrl matching pattern.
const redirectUrlPattern = new RegExp('(?=^.{1,256}$)' + //length limit
  '(^(https?:\\/\\/)' + // protocol
  '(?:(?!\\.|-)([a-zA-Z0-9_\\-]\\.?)+)' + // domain name(Matches any FQDNs or non-FQDNs except those starting with '.' or '-', in accordance with the oauth spec guidelines for redirect_url registration) 
  '(\\:\\d+)?' + // port
  '(\\/auth\\/add_oauth_token))');// redirectUrl path has to be /auth/add_oauth_token

const embeddedWhiteSpace = /\s/;

function validateUrl(value: string): boolean {
  return !!urlPattern.test(value);
}

function validateRedirectUrl(value: string): boolean {
  if (value.startsWith('http://')) {
    // revised from http://scratch99.com/web-development/javascript/how-to-get-the-domain-from-a-url/
    var domain = value.replace('http://', '').split(/[/?#:]/)[0];
    if (domain !== 'localhost') {
      return false;
    }
  }
  return !!redirectUrlPattern.test(value);
}

export function validateNoEmbeddedWhiteSpaceCI(value: string): string {
  if (!value || embeddedWhiteSpace.test(value)) {
    return Messages.invalidCI();
  }
  return '';
}

export function validateNoEmbeddedWhiteSpaceCS(value: string): string {
  if (!value || embeddedWhiteSpace.test(value)) {
    return Messages.invalidCS();
  }
  return '';
}

export function validateInstanceUrl(value: string): string {
  let v: string;
  if (value && (v = value.trim()) && !validateUrl(v)) {
    return Messages.invalidUrl();
  }
  return '';
}

export function validateRedirectUri(value: string): string {
  if (!value || !validateRedirectUrl(value.trim())) {
    return Messages.invalidRedirectUrl();
  }
  return '';
}

export class AddOAuthClientDialog extends React.Component<AddOAuthClientDialogProps, AddOAuthClientDialogState> {
  constructor(props: AddOAuthClientDialogProps) {
    super(props);
  }

  handleSubmit(client: OAuthClientSetting, formik: FormikHelpers<OAuthClientSetting>) {
    formik.setSubmitting(true);
    this.props.addOAuthClientSubmit(client);
    formik.setSubmitting(false);
    this.props.close(true);
  }

  private getConnectionTypeDropdownSection(formik: FormikProps<AddOAuthClientDialogState>, credentialTypesList: ListItem[]): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='dbClass'>{Messages.oauthClientDbClass()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikSingleSelectDropdown
            name='dbClass'
            list={<List items={credentialTypesList} className={UserSettingsStyle.dropdownList} />}
            type={DropdownButtonType.Outline}
            label={''}
            className={dropdownStyle}
            testId={'add-oauth-client-choose-existing-dbclass'}
            value={formik.values.dbClass}
            valid={formik.values.dbClass !== Messages.addOAuthClient_chooseExisting()}
            disabled={!_.isUndefined(this.props.hardcodeConnectionType)}
          />
        </td>
      </tr>
    );
  }

  private getInstanceUrlSection(): JSX.Element {
    if (!this.props.showInstanceUrlSection) return <></>;
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='instanceUrl'>{Messages.oauthClientInstanceUrl()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='instanceUrl'
            testId='instanceUrl'
            label=''
            validate={validateInstanceUrl}
            className={textFieldWidget}
            showValidationMessage={true}
            placeholder='Optional'
          />
        </td>
      </tr>
    );
  }

  private getClientIdSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='oauthClientId'>{Messages.oauthClientId()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='oauthClientId'
            testId='oauthClientId'
            label=''
            validate={validateNoEmbeddedWhiteSpaceCI}
            className={textFieldWidget}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  }

  private getClientSecretSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='oauthClientSecret'>{Messages.oauthClientSecret()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='oauthClientSecret'
            testId='oauthClientSecret'
            label=''
            validate={validateNoEmbeddedWhiteSpaceCS}
            className={textFieldWidget}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  }

  private getRedirectUrlSection(): JSX.Element {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='oauthClientRedirectUri'>{Messages.oauthRedirectUri()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide' title={Messages.oauthRedirectUri_input_tooltip()}>
          <FormikTextBox
            name='oauthClientRedirectUri'
            testId='oauthClientRedirectUri'
            label=''
            validate={validateRedirectUri}
            className={textFieldWidget}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  }

  public render() {
    const initialValues = {
      dbClass: this.props.hardcodeConnectionType ?
        OAuthCredentialsService.getDisplayNameFromType(this.props.hardcodeConnectionType) :
        Messages.addOAuthClient_chooseExisting(),
      instanceUrl: '',
      oauthClientId: '',
      oauthClientSecret: '',
      oauthClientRedirectUri: ''
    };
    const allCredentialTypes = OAuthCredentialsService.getAllOAuthCredentialTypes();
    const credentialTypesList = allCredentialTypes
      .filter(type => !OAuthCredentialsService.supportsGenericAuth(type.type))
      .map<ListItem>(typeInfo => { return { value: OAuthCredentialsService.getDisplayNameFromInfo(typeInfo) }; });
    return (
      <Formik
        initialValues={initialValues}
        onSubmit={(values: OAuthClientSetting, formik: FormikHelpers<OAuthClientSetting>) => {
          // clean up any of the "display names" used for dbClass so that they match their internal 'type' strings.
          const matchingCredentialType = allCredentialTypes.filter(
            typeInfo => OAuthCredentialsService.getDisplayNameFromInfo(typeInfo) === values.dbClass);
          if (matchingCredentialType.length > 0) {
            // there should only be one match, if there are more, we just match to the first one.
            values.dbClass = matchingCredentialType[0].type;
          }
          this.handleSubmit(values, formik);
        }}
      >
        {(formik: FormikProps<AddOAuthClientDialogState>) => (
          <form onSubmit={formik.handleSubmit} data-tb-test-id='add-oauth-client-dialog'>
            <div className='tb-dialog-title'>{Messages.oauthClientDialogTitle()}</div>
            <table className='tb-form-table'>
              <tbody>
                {this.getConnectionTypeDropdownSection(formik, credentialTypesList)}
                {this.getInstanceUrlSection()}
                {this.getClientIdSection()}
                {this.getClientSecretSection()}
                {this.getRedirectUrlSection()}
              </tbody>
            </table>
            <div className='tb-dialog-actions'>
              <ButtonWidget buttonType={ButtonType.Outline}
                testId='add-oauth-client-dialog-dismiss'
                onClick={() => this.props.dismiss()}
                disabled={formik.isSubmitting}
                type='button'>{Messages.dismiss()}
              </ButtonWidget>
              <ButtonWidget buttonType={ButtonType.Primary}
                testId='add-oauth-client-dialog-add-oauth-client-button'
                className='tb-margin-left-wide'
                disabled={!formik.dirty || !formik.isValid || formik.isSubmitting || formik.values.dbClass === Messages.addOAuthClient_chooseExisting()}
                onFocus={() => true}
                type='submit'>{Messages.addOAuthClient_action()}
              </ButtonWidget>
            </div>
          </form>
        )}
      </Formik>
    );
  }
}
